import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type {
  FuelGradeOption,
  FuelMerchantCreate,
  FuelMerchantListResponse,
  FuelMerchantRow,
  FuelMerchantUpdate,
  PriceTrendResponse,
  PriceZoneOption,
} from '@/types/merchants';

const FUEL_MERCHANTS_KEY = ['merchants', 'fuel'] as const;

export function usePriceZones() {
  return useQuery<PriceZoneOption[]>({
    queryKey: ['merchants', 'price-zones'],
    queryFn: async () => {
      const { data } = await api.get<PriceZoneOption[]>('/merchants/price-zones');
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useFuelGrades() {
  return useQuery<FuelGradeOption[]>({
    queryKey: ['merchants', 'fuel-grades'],
    queryFn: async () => {
      const { data } = await api.get<FuelGradeOption[]>('/merchants/fuel-grades');
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

interface FuelMerchantsParams {
  q?: string;
  activeOnly?: boolean;
  limit?: number;
  offset?: number;
}

export function useFuelMerchants(params: FuelMerchantsParams = {}) {
  const { q, activeOnly = true, limit = 50, offset = 0 } = params;
  return useQuery<FuelMerchantListResponse>({
    queryKey: [...FUEL_MERCHANTS_KEY, { q, activeOnly, limit, offset }],
    queryFn: async () => {
      const { data } = await api.get<FuelMerchantListResponse>('/merchants/fuel', {
        params: {
          q: q || undefined,
          active_only: activeOnly,
          limit,
          offset,
        },
      });
      return data;
    },
    staleTime: 30_000,
  });
}

export function useCreateFuelMerchant() {
  const qc = useQueryClient();
  return useMutation<FuelMerchantRow, Error, FuelMerchantCreate>({
    mutationFn: async (payload) => {
      const { data } = await api.post<FuelMerchantRow>('/merchants/fuel', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FUEL_MERCHANTS_KEY });
    },
  });
}

export function useUpdateFuelMerchant() {
  const qc = useQueryClient();
  return useMutation<
    FuelMerchantRow,
    Error,
    { merchantId: number; patch: FuelMerchantUpdate }
  >({
    mutationFn: async ({ merchantId, patch }) => {
      const { data } = await api.patch<FuelMerchantRow>(
        `/merchants/fuel/${merchantId}`,
        patch,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FUEL_MERCHANTS_KEY });
    },
  });
}

interface PriceTrendParams {
  zoneId?: number;
  merchantId?: number;
  grade?: string;
  months?: number;
}

export function usePriceTrend(params: PriceTrendParams) {
  const { zoneId, merchantId, grade, months = 24 } = params;
  const enabled = Boolean(grade) && (zoneId != null || merchantId != null);
  return useQuery<PriceTrendResponse>({
    queryKey: ['merchants', 'price-trend', { zoneId, merchantId, grade, months }],
    enabled,
    queryFn: async () => {
      const { data } = await api.get<PriceTrendResponse>('/merchants/price-trend', {
        params: {
          zone_id: zoneId ?? undefined,
          merchant_id: merchantId ?? undefined,
          grade,
          months,
        },
      });
      return data;
    },
    staleTime: 60_000,
  });
}
